import React, { useState, useEffect, useRef } from 'react';
import { X, Share2, Copy, Check, Clock, Mail, Loader2, AlertCircle, Trash2, Link2, Send } from 'lucide-react';
import Modal from '@shared/ui/Modal';
import NotesV2ApiService, { NoteShare } from '../../services/notesV2Api';
import toast from 'react-hot-toast';
import Button from '@shared/ui/Button';
import ConfirmationDialog from '../ConfirmationDialog';
import { formatDateTime as formatDate } from '../../utils/dateUtils';

interface ShareNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  noteId: string;
  noteTitle?: string;
}

// Helper to validate email format
const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

const ShareNoteModal: React.FC<ShareNoteModalProps> = ({
  isOpen,
  onClose,
  noteId,
  noteTitle
}) => {
  const [shares, setShares] = useState<NoteShare[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [copiedToken, setCopiedToken] = useState<string | null>(null);
  const [revokingShareId, setRevokingShareId] = useState<string | null>(null);
  
  // Form state for new share - now supports multiple emails
  const [recipientEmails, setRecipientEmails] = useState<string[]>([]);
  const [currentEmailInput, setCurrentEmailInput] = useState('');
  const [emailInputError, setEmailInputError] = useState<string | null>(null);
  const [expiresInDays, setExpiresInDays] = useState(7);
  const emailInputRef = useRef<HTMLInputElement>(null);
  
  // State for displaying created share URL
  const [createdShareUrl, setCreatedShareUrl] = useState<string | null>(null);
  const [createdShareCopied, setCreatedShareCopied] = useState(false);
  
  // Maximum recipients per share (to differentiate from Outreach feature)
  const MAX_RECIPIENTS = 20;

  // Fetch existing shares
  useEffect(() => {
    if (isOpen && noteId) {
      fetchShares();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, noteId]);

  const fetchShares = async () => {
    try {
      setLoading(true);
      const sharesData = await NotesV2ApiService.getNoteShares(noteId);
      setShares(sharesData);
    } catch (error) {
      console.error('Error fetching shares:', error);
      toast.error('Failed to load share links');
    } finally {
      setLoading(false);
    }
  };

  const addEmail = (email: string) => {
    const trimmedEmail = email.trim().toLowerCase();
    setEmailInputError(null);
    
    if (!trimmedEmail) return;
    
    if (!isValidEmail(trimmedEmail)) {
      setEmailInputError('Please enter a valid email address');
      return;
    }
    
    if (recipientEmails.includes(trimmedEmail)) {
      setEmailInputError('This email has already been added');
      return;
    }
    
    if (recipientEmails.length >= MAX_RECIPIENTS) {
      setEmailInputError(`Maximum ${MAX_RECIPIENTS} recipients per share. For larger distributions, use Outreach.`);
      return;
    }
    
    setRecipientEmails([...recipientEmails, trimmedEmail]);
    setCurrentEmailInput('');
  };

  const removeEmail = (emailToRemove: string) => {
    setRecipientEmails(recipientEmails.filter(email => email !== emailToRemove));
  };

  const handleEmailInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === 'Tab') {
      e.preventDefault();
      addEmail(currentEmailInput);
    } else if (e.key === 'Backspace' && !currentEmailInput && recipientEmails.length > 0) {
      // Remove last email if backspace pressed on empty input
      setRecipientEmails(recipientEmails.slice(0, -1));
    }
  };

  const handleEmailInputBlur = () => {
    if (currentEmailInput.trim()) {
      addEmail(currentEmailInput);
    }
  };

  const handleCreateShare = async (sendEmails: boolean) => {
    // Add any remaining input as an email
    const finalEmails = [...recipientEmails];
    if (currentEmailInput.trim()) {
      const trimmedEmail = currentEmailInput.trim().toLowerCase();
      if (isValidEmail(trimmedEmail) && !finalEmails.includes(trimmedEmail)) {
        finalEmails.push(trimmedEmail);
      }
    }
    
    if (finalEmails.length === 0) {
      toast.error('Please add at least one recipient email');
      return;
    }

    // Validate recipient limit
    if (finalEmails.length > MAX_RECIPIENTS) {
      toast.error(`Maximum ${MAX_RECIPIENTS} recipients per share. For larger distributions, use Outreach.`);
      return;
    }

    try {
      setCreating(true);
      
      // Calculate expiration date
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + expiresInDays);
      
      // Use new multi-recipient format with single share link
      const recipients = finalEmails.map(email => ({ email, notification_type: 'email' }));
      const response = await NotesV2ApiService.createNoteShareMultiRecipient(
        noteId,
        recipients,
        expiresAt.toISOString(),
        sendEmails
      );

      const createdCount = response.recipients.length;
      
      // Use short URL if available, otherwise fall back to full URL
      const shareUrl = response.short_url 
        ? (response.short_url.startsWith('http') ? response.short_url : `${window.location.origin}${response.short_url}`)
        : (response.share_url.startsWith('http') ? response.share_url : `${window.location.origin}${response.share_url}`);
      
      // Store the share URL for display
      setCreatedShareUrl(shareUrl);
      
      if (sendEmails) {
        toast.success(`Share link created and email${createdCount > 1 ? 's' : ''} sent to ${createdCount} recipient${createdCount > 1 ? 's' : ''}!`);
      } else {
        // Copy to clipboard when not sending emails
        await navigator.clipboard.writeText(shareUrl);
        setCreatedShareCopied(true);
        setTimeout(() => setCreatedShareCopied(false), 2000);
        toast.success('Link created and copied to clipboard');
      }
      
      // Reset form and refresh shares
      setRecipientEmails([]);
      setCurrentEmailInput('');
      fetchShares();
      
    } catch (error: any) {
      console.error('Error creating share:', error);
      toast.error(error.response?.data?.error || 'Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const handleCopyLink = async (shortId: string | null | undefined, token: string) => {
    // Use short URL if available, otherwise fall back to full token URL
    const shareUrl = shortId 
      ? `${window.location.origin}/n/${shortId}`
      : `${window.location.origin}/feedback/${token}`;
    await navigator.clipboard.writeText(shareUrl);
    setCopiedToken(token);
    toast.success('Link copied to clipboard');
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const handleRevokeShare = async () => {
    if (!revokingShareId) return;
    
    try {
      await NotesV2ApiService.revokeShare(revokingShareId);
      toast.success('Share link revoked');
      setRevokingShareId(null);
      fetchShares();
    } catch (error) {
      console.error('Error revoking share:', error);
      toast.error('Failed to revoke share link');
    }
  };


  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
      case 'active':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-warning-100 text-warning-800 dark:bg-warning-900/30 dark:text-warning-400">
            <Clock className="w-3 h-3 mr-1" />
            Pending
          </span>
        );
      case 'viewed':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-info-100 text-info-800 dark:bg-info-900/30 dark:text-info-400">
            <Check className="w-3 h-3 mr-1" />
            Viewed
          </span>
        );
      case 'responded':
      case 'all_responded':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400">
            <Check className="w-3 h-3 mr-1" />
            Responded
          </span>
        );
      case 'has_responses':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-800 dark:bg-success-900/30 dark:text-success-400">
            <Check className="w-3 h-3 mr-1" />
            Some Responded
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-background-tertiary text-text-tertiary dark:bg-background-dark-tertiary dark:text-text-dark-tertiary">
            <AlertCircle className="w-3 h-3 mr-1" />
            Expired
          </span>
        );
      case 'revoked':
        return (
          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-error-100 text-error-800 dark:bg-error-900/30 dark:text-error-400">
            <X className="w-3 h-3 mr-1" />
            Revoked
          </span>
        );
      default:
        return null;
    }
  };

  const handleResetForm = () => {
    setRecipientEmails([]);
    setCurrentEmailInput('');
    setEmailInputError(null);
    setCreatedShareUrl(null);
    setCreatedShareCopied(false);
  };

  if (!isOpen) return null;

  const activeShares = shares.filter(s => 
    s.status === 'pending' || s.status === 'active' || s.status === 'viewed' || 
    s.status === 'has_responses' || s.status === 'all_responded'
  );
  const inactiveShares = shares.filter(s => s.status === 'expired' || s.status === 'revoked');

  return (
    <>
      <Modal
        isOpen={isOpen}
        title="Share note for feedback"
        description={noteTitle || undefined}
        onClose={onClose}
        size="lg"
        footer={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Explanatory Section */}
            <div className="bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg p-4 mb-6">
              <h3 className="text-sm font-semibold text-info-900 dark:text-info-100 mb-2">
                How Share for Feedback Works
              </h3>
              <ul className="text-sm text-info-800 dark:text-info-200 space-y-1.5">
                <li>• Share this note with specific people to request their feedback and input</li>
                <li>• Email addresses are required for security - recipients must verify their email to access the note</li>
                <li>• All recipients share one link, but each must enter their email when they visit</li>
                <li>• Send automatic emails or copy the link to share manually via your preferred channel</li>
              </ul>
            </div>

            {/* Create New Share Section - Always Visible */}
            <div className="card p-4 mb-6">
              <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary mb-4">
                Create Share Links
              </h3>
              
              <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
                      Recipient Emails
                      <span className="text-xs text-text-tertiary dark:text-text-dark-tertiary font-normal ml-2">
                        (used to verify access to the note)
                      </span>
                    </label>
                    
                    {/* Email Tags Container */}
                    <div 
                      className="min-h-[42px] p-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-transparent cursor-text"
                      onClick={() => emailInputRef.current?.focus()}
                    >
                      <div className="flex flex-wrap gap-2">
                        {/* Email Tags */}
                        {recipientEmails.map((email) => (
                          <span 
                            key={email}
                            className="inline-flex items-center gap-1 px-2 py-1 text-sm bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-300 rounded-md"
                          >
                            <Mail className="w-3 h-3" />
                            <span className="max-w-[200px] truncate">{email}</span>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeEmail(email);
                              }}
                              className="ml-1 p-0.5 hover:bg-primary-200 dark:hover:bg-primary-800 rounded transition-colors"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </span>
                        ))}
                        
                        {/* Email Input */}
                        <input
                          ref={emailInputRef}
                          type="text"
                          value={currentEmailInput}
                          onChange={(e) => {
                            setCurrentEmailInput(e.target.value);
                            setEmailInputError(null);
                          }}
                          onKeyDown={handleEmailInputKeyDown}
                          onBlur={handleEmailInputBlur}
                          placeholder={recipientEmails.length === 0 ? "Enter email addresses..." : "Add another..."}
                          className="flex-1 min-w-[150px] py-1 bg-transparent text-text-primary dark:text-text-dark-primary placeholder-text-tertiary dark:placeholder-text-dark-tertiary outline-none border-none"
                        />
                      </div>
                    </div>
                    
                    {/* Error message */}
                    {emailInputError && (
                      <p className="text-xs text-error-600 dark:text-error-400 mt-1">
                        {emailInputError}
                      </p>
                    )}
                    
                    <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-1">
                      Press Enter, Tab, or comma to add each email. All recipients share a single link but each must verify with their email.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                      Links Expire In
                    </label>
                    <select
                      value={expiresInDays}
                      onChange={(e) => setExpiresInDays(Number(e.target.value))}
                      className="w-full px-4 py-2 border border-border-light dark:border-border-dark rounded-lg bg-white dark:bg-background-dark-secondary text-text-primary dark:text-text-dark-primary focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    >
                      <option value={1}>1 day</option>
                      <option value={3}>3 days</option>
                      <option value={7}>7 days</option>
                      <option value={14}>14 days</option>
                      <option value={30}>30 days</option>
                      <option value={90}>90 days</option>
                    </select>
                  </div>

                  {/* Recipient Limit Info */}
                  <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    {recipientEmails.length}/{MAX_RECIPIENTS} recipients (max {MAX_RECIPIENTS} per share)
                  </p>
                
                  <div className="flex flex-col gap-3 mt-4">
                  {/* Action Buttons Row */}
                  <div className="flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleResetForm}
                      disabled={creating}
                    >
                      Clear
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => handleCreateShare(false)}
                      disabled={creating || (recipientEmails.length === 0 && !currentEmailInput.trim())}
                    >
                      {creating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Copy className="w-4 h-4 mr-2" />
                      )}
                      Copy Link
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={() => handleCreateShare(true)}
                      disabled={creating || (recipientEmails.length === 0 && !currentEmailInput.trim())}
                    >
                      {creating ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Send className="w-4 h-4 mr-2" />
                      )}
                      Send Email
                    </Button>
                  </div>
                  
                  {/* Display Created Share URL */}
                  {createdShareUrl && (
                    <div className="p-3 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Check className="w-4 h-4 text-success-600 dark:text-success-400" />
                        <span className="text-sm font-medium text-success-800 dark:text-success-300">Share link created!</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
                        <code className="flex-1 text-xs bg-white dark:bg-background-dark-secondary px-2 py-1 rounded text-text-secondary dark:text-text-dark-secondary truncate">
                          {createdShareUrl}
                        </code>
                        <button
                          type="button"
                          onClick={async () => {
                            await navigator.clipboard.writeText(createdShareUrl);
                            setCreatedShareCopied(true);
                            setTimeout(() => setCreatedShareCopied(false), 2000);
                            toast.success('Link copied to clipboard');
                          }}
                          className="flex items-center gap-1 px-2 py-1 text-sm text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200 transition-colors flex-shrink-0"
                        >
                          {createdShareCopied ? (
                            <>
                              <Check className="w-4 h-4" />
                              <span>Copied!</span>
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4" />
                              <span>Copy</span>
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                  </div>
                </div>
              </div>

            {/* Divider */}
            {shares.length > 0 && (
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border-light dark:border-border-dark"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="px-3 bg-white dark:bg-background-dark-secondary text-xs text-text-tertiary dark:text-text-dark-tertiary">
                    Existing Share Links
                  </span>
                </div>
              </div>
            )}

            {/* Existing Shares */}
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
              </div>
            ) : shares.length === 0 ? (
              <div className="text-center py-6 px-4 rounded-lg bg-background-tertiary dark:bg-background-dark-tertiary text-text-tertiary dark:text-text-dark-tertiary">
                <Share2 className="w-10 h-10 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No share links created yet</p>
                <p className="text-xs mt-1 opacity-75">Your created share links will appear here</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Active Shares */}
                {activeShares.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-text-primary dark:text-text-dark-primary mb-3">
                      Active Links ({activeShares.length})
                    </h3>
                    <div className="space-y-3">
                      {activeShares.map((share) => (
                        <div 
                          key={share.id} 
                          className="p-4 rounded-lg border border-border-light dark:border-border-dark bg-background-tertiary dark:bg-background-dark-tertiary"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Share2 className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                                <span className="text-sm font-medium text-text-primary dark:text-text-dark-primary">
                                  {share.recipients?.length > 0 
                                    ? `${share.recipients.length} recipient${share.recipients.length > 1 ? 's' : ''}`
                                    : 'Shared'
                                  }
                                </span>
                                {getStatusBadge(share.status)}
                                {share.send_email === false && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-background-tertiary text-text-tertiary dark:bg-background-dark-tertiary dark:text-text-dark-tertiary">
                                    Manual share
                                  </span>
                                )}
                              </div>
                              
                              {/* Recipients list for multi-recipient shares */}
                              {share.recipients?.length > 0 && (
                                <div className="mb-2 space-y-1">
                                  {share.recipients.map((recipient) => (
                                    <div key={recipient.id} className="flex items-center gap-2 text-xs">
                                      <Mail className="w-3 h-3 text-text-tertiary dark:text-text-dark-tertiary" />
                                      <span className="text-text-secondary dark:text-text-dark-secondary">
                                        {recipient.email}
                                      </span>
                                      {recipient.is_user && recipient.user_name && (
                                        <span className="text-text-tertiary dark:text-text-dark-tertiary">
                                          ({recipient.user_name})
                                        </span>
                                      )}
                                      {getStatusBadge(recipient.status)}
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Share Link Display */}
                              <div className="flex items-center gap-2 mb-2 p-2 rounded bg-background-tertiary dark:bg-background-dark-quaternary">
                                <Link2 className="w-3 h-3 text-text-tertiary dark:text-text-dark-tertiary flex-shrink-0" />
                                <span className="text-xs text-text-secondary dark:text-text-dark-secondary">Link:</span>
                                <code className="flex-1 text-xs text-text-secondary dark:text-text-dark-secondary truncate">
                                  {share.short_id 
                                    ? `${window.location.origin}/n/${share.short_id}`
                                    : `${window.location.origin}/feedback/${share.token}`
                                  }
                                </code>
                                <button
                                  onClick={() => handleCopyLink(share.short_id, share.token)}
                                  className="flex items-center gap-1 px-2 py-0.5 text-xs text-primary-600 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-200 transition-colors flex-shrink-0"
                                >
                                  {copiedToken === share.token ? (
                                    <>
                                      <Check className="w-3 h-3" />
                                      <span>Copied!</span>
                                    </>
                                  ) : (
                                    <>
                                      <Copy className="w-3 h-3" />
                                      <span>Copy</span>
                                    </>
                                  )}
                                </button>
                              </div>
                              
                              <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary space-y-1">
                                <p>Created: {formatDate(share.created_at)} by {share.created_by_name}</p>
                                <p>Expires: {formatDate(share.expires_at)}</p>
                                {share.responded_count > 0 && (
                                  <p className="text-success-600 dark:text-success-400">
                                    {share.responded_count} of {share.recipient_count} responded
                                  </p>
                                )}
                              </div>
                              
                              {/* Show responses from recipients */}
                              {share.recipients?.filter(r => r.response).map((recipient) => (
                                <div key={`response-${recipient.id}`} className="mt-3 p-3 bg-white dark:bg-background-dark-secondary rounded border border-border-light dark:border-border-dark">
                                  <p className="text-xs font-medium text-text-secondary dark:text-text-dark-secondary mb-1">
                                    Response from {recipient.email}:
                                  </p>
                                  <p className="text-sm text-text-primary dark:text-text-dark-primary">
                                    {recipient.response?.content}
                                  </p>
                                </div>
                              ))}
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              <button
                                onClick={() => setRevokingShareId(share.id)}
                                className="p-2 text-text-tertiary hover:text-error-500 dark:text-text-dark-tertiary dark:hover:text-error-400 transition-colors rounded"
                                title="Revoke share"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Inactive Shares */}
                {inactiveShares.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-text-tertiary dark:text-text-dark-tertiary mb-3">
                      Inactive Links ({inactiveShares.length})
                    </h3>
                    <div className="space-y-3 opacity-60">
                      {inactiveShares.map((share) => (
                        <div 
                          key={share.id} 
                          className="p-4 rounded-lg border border-border-light dark:border-border-dark"
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Share2 className="w-4 h-4 text-text-tertiary dark:text-text-dark-tertiary" />
                            <span className="text-sm text-text-secondary dark:text-text-dark-secondary">
                              {share.recipients?.length > 0 
                                ? `${share.recipients.length} recipient${share.recipients.length > 1 ? 's' : ''}`
                                : 'Shared'
                              }
                            </span>
                            {getStatusBadge(share.status)}
                          </div>
                          <div className="text-xs text-text-tertiary dark:text-text-dark-tertiary">
                            <p>Created: {formatDate(share.created_at)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

      </Modal>

      <ConfirmationDialog
        isOpen={!!revokingShareId}
        title="Revoke Share Link"
        message="Are you sure you want to revoke this share link? The recipient will no longer be able to access the note or submit feedback."
        confirmText="Revoke"
        onConfirm={handleRevokeShare}
        onCancel={() => setRevokingShareId(null)}
        variant="danger"
      />
    </>
  );
};

export default ShareNoteModal;
